import { cn } from '@simplywallst/ui-core/next';

import { RouterLink } from '@redux-first-router-link';

import { formatterCurrency, formatterPercentage } from '@/utilities/format';
import { getTradingItemCurrencyISO } from '@/utilities/getTradingItemCurrencyISO';

import { CompanyIcon } from '@/components/CompanyIcon';

import type { PortfolioDetailedReturns } from '../../hooks/usePortfolioDetailedReturns';

import { TABLE_OVERVIEW_COLUMN_LENGTH } from '../TableOverview/utils';
import { getDateRangeString } from '../../utils';

import { HeaderCell } from './HeaderCell';
import { PositiveNegativeValue } from '../PositiveNegativeValue';
import { Tooltip } from '../Tooltip';
import type { PortfolioType } from '../../../HoldingsTable/hooks';
type Props = {
  currencyISO: string;
  holding: PortfolioDetailedReturns['holdings'][number];
  portfolioType: PortfolioType;
};

export const HoldingOverview = ({
  currencyISO,
  holding,
  portfolioType,
}: Props) => {
  const isHoldingsPortfolio = portfolioType !== 'transaction-portfolio';
  const cell = cn(
    'border-b border-l-0 border-r border-t-0 border-solid border-soft px-x2 py-x1_5'
  );

  const bodyCell = cn(cell, 'text-xs text-solid');

  const headerCell = cn(
    cell,
    'bg-surface-2 align-bottom font-strong leading-tight'
  );

  const company = holding.company;
  const holdingCurrencyISO =
    getTradingItemCurrencyISO(company.data.currencyInfo) ?? 'XXX';

  return (
    <tbody>
      <tr id={holding.company.uniqueSymbol} className="scroll-mt-x3">
        <th
          colSpan={TABLE_OVERVIEW_COLUMN_LENGTH}
          className="h-x5 border-x-0 border-b border-t-0 border-solid border-soft"
        />
      </tr>
      <tr aria-label={`${holding.company.uniqueSymbol} Summary`}>
        <th
          colSpan={TABLE_OVERVIEW_COLUMN_LENGTH}
          className="top-[63px] z-1 h-[58px] border-b border-l-0 border-r border-t-0 border-solid border-soft bg-surface-1 px-x2 py-x1 text-left xl:sticky"
        >
          <div className="grid grid-cols-[43px_auto] gap-x1_5">
            <CompanyIcon
              tickerSymbol={holding.symbol}
              website={holding.company.info?.url}
              size={40}
            />
            <div className="grid grid-rows-[auto_auto] items-center gap-x0">
              <RouterLink
                data-cy-id="ticker-symbol"
                noUnderline
                to={holding.company.canonicalURL}
                className="text-xs font-bold"
              >
                {holding.company.name}
              </RouterLink>
              <span className="text-tiny font-normal text-soft">
                {holding.company.uniqueSymbol}
              </span>
            </div>
          </div>
        </th>
      </tr>
      <tr aria-label={`${holding.company.uniqueSymbol} Returns Summary`}>
        <th
          colSpan={TABLE_OVERVIEW_COLUMN_LENGTH}
          className="border-x-0 border-b border-t-0 border-solid border-soft px-x2 py-x0_75 text-left text-tiny font-strong text-soft"
        >
          <span className="uppercase">Returns Summary</span>
          {` `}
          <span className="ml-x0_75 text-softer">
            {getDateRangeString(
              holding.detailedReturns.startDate,
              holding.detailedReturns.endDate
            )}
          </span>
        </th>
      </tr>
      <tr className="text-right text-tiny text-soft [&_th:last-child]:text-left">
        <th colSpan={2} className={headerCell}>
          <HeaderCell
            label={
              <Tooltip
                label="Shares"
                tip="The number of shares held at the end of the selected period."
              />
            }
          />
        </th>
        <th className={headerCell}>
          <HeaderCell
            label={
              <Tooltip
                label="Price"
                tip="The stock's last closing price on the end date of the selected period."
              />
            }
          />
        </th>
        <th className={headerCell}>
          <HeaderCell
            label={
              <Tooltip
                label="Value"
                tip="The market value of shares held at the end of the selected period."
              />
            }
            currencyISO={currencyISO}
          />
        </th>
        <th className={headerCell}>
          <HeaderCell
            label={
              <Tooltip
                label="Cost Basis"
                tip="The total purchase cost of shares held at the end of the selected period."
              />
            }
            currencyISO={currencyISO}
          />
        </th>
        <th className={headerCell}>
          <HeaderCell
            label={
              <Tooltip
                label="Unrealized"
                tip="The gain/loss on unsold shares based on their value at the end of the selected period."
              />
            }
            currencyISO={currencyISO}
          />
        </th>
        <th className={headerCell}>
          <HeaderCell
            label={
              <Tooltip
                label="Realized"
                tip="The gain/loss from shares sold during the selected period."
              />
            }
            currencyISO={currencyISO}
          />
        </th>
        <th className={headerCell}>
          <HeaderCell
            label={
              <Tooltip
                label="Dividends"
                tip="Total dividends received during the selected period."
              />
            }
            currencyISO={currencyISO}
          />
        </th>
        <th className={headerCell}>
          <HeaderCell
            label={
              <Tooltip
                label="Currency Gains"
                tip="Foreign exchange gain/loss from shares sold during the selected period."
              />
            }
            currencyISO={currencyISO}
          />
        </th>
        <th className={headerCell}>
          <HeaderCell
            label={
              <Tooltip
                label="Total Gains"
                tip="The overall gain/loss, including realised, unrealised, dividends, and currency effects, in the selected period."
              />
            }
            currencyISO={currencyISO}
          />
        </th>
        <th className={headerCell}>
          <HeaderCell
            label={
              <Tooltip
                label="Annualized (IRR)"
                tip="The annualised return rate for the selected period, calculated using the Internal Rate of Return (IRR)."
              />
            }
          />
        </th>
      </tr>
      <tr className="text-right text-tiny text-soft">
        <td colSpan={2} className={bodyCell}>
          {holding.detailedReturns.units}
        </td>
        <td className={bodyCell}>
          {formatterCurrency(holding.detailedReturns.price, holdingCurrencyISO)}
        </td>
        <td className={bodyCell}>
          {formatterCurrency(holding.detailedReturns.value, 'XXX')}
        </td>
        <td className={bodyCell}>
          {formatterCurrency(holding.detailedReturns.costBasis, 'XXX')}
        </td>
        <td className={bodyCell}>
          {formatterCurrency(holding.detailedReturns.unrealizedGains, 'XXX')}
        </td>
        {isHoldingsPortfolio ? (
          <>
            <td className={bodyCell}>
              <Tooltip
                label="No data"
                tip="This metric is only available for transaction portfolios. Create a transaction portfolio to see your true returns."
              />
            </td>
            <td className={bodyCell}>
              <Tooltip
                label="No data"
                tip="This metric is only available for transaction portfolios. Create a transaction portfolio to see your true returns."
              />
            </td>
            <td className={bodyCell}>
              <Tooltip
                label="No data"
                tip="This metric is only available for transaction portfolios. Create a transaction portfolio to see your true returns."
              />
            </td>
            <td className={bodyCell}>
              {formatterCurrency(holding.detailedReturns.totalGains, 'XXX')}
            </td>
            <td className={bodyCell}>
              <Tooltip
                label="No data"
                tip="This metric is only available for transaction portfolios. Create a transaction portfolio to see your true returns."
              />
            </td>
          </>
        ) : (
          <>
            <td className={bodyCell}>
              {formatterCurrency(holding.detailedReturns.realizedGains, 'XXX')}
            </td>
            <td className={bodyCell}>
              {formatterCurrency(holding.detailedReturns.dividends, 'XXX')}
            </td>
            <td className={bodyCell}>
              {formatterCurrency(holding.detailedReturns.currencyGains, 'XXX')}
            </td>
            <td className={bodyCell}>
              {formatterCurrency(holding.detailedReturns.totalGains, 'XXX')}
            </td>
            <td className={bodyCell}>
              {holding.detailedReturns.annualisedReturn ? (
                formatterPercentage(holding.detailedReturns.annualisedReturn)
              ) : (
                <Tooltip
                  label="No data"
                  tip="To calculate IRR, the position must be held for at least 90 days, and the selected period must also be 90 days or more."
                />
              )}
            </td>
          </>
        )}
      </tr>
      <tr className="text-right text-tiny text-soft">
        <td colSpan={2} className={bodyCell}></td>
        <td className={bodyCell}></td>
        <td className={bodyCell}></td>
        <td className={bodyCell}></td>
        <td className={bodyCell}>
          <PositiveNegativeValue
            value={holding.detailedReturns.unrealizedGainsPercent}
          >
            {formatterPercentage(
              holding.detailedReturns.unrealizedGainsPercent
            )}
          </PositiveNegativeValue>
        </td>
        {isHoldingsPortfolio ? (
          <>
            <td className={bodyCell}>
              <Tooltip
                label="No data"
                tip="This metric is only available for transaction portfolios. Create a transaction portfolio to see your true returns."
              />
            </td>
            <td className={bodyCell}>
              <Tooltip
                label="No data"
                tip="This metric is only available for transaction portfolios. Create a transaction portfolio to see your true returns."
              />
            </td>
            <td className={bodyCell}>
              <Tooltip
                label="No data"
                tip="This metric is only available for transaction portfolios. Create a transaction portfolio to see your true returns."
              />
            </td>
            <td className={bodyCell}>
              <PositiveNegativeValue
                value={holding.detailedReturns.totalGainsPercent}
              >
                {formatterPercentage(holding.detailedReturns.totalGainsPercent)}
              </PositiveNegativeValue>
            </td>
            <td className={bodyCell}></td>
          </>
        ) : (
          <>
            <td className={bodyCell}>
              <PositiveNegativeValue
                value={holding.detailedReturns.realizedGainsPercent}
              >
                {formatterPercentage(
                  holding.detailedReturns.realizedGainsPercent
                )}
              </PositiveNegativeValue>
            </td>
            <td className={bodyCell}>
              <PositiveNegativeValue
                value={holding.detailedReturns.dividendsPercent}
              >
                {formatterPercentage(holding.detailedReturns.dividendsPercent)}
              </PositiveNegativeValue>
            </td>
            <td className={bodyCell}>
              <PositiveNegativeValue
                value={holding.detailedReturns.currencyGainsPercent}
              >
                {formatterPercentage(
                  holding.detailedReturns.currencyGainsPercent
                )}
              </PositiveNegativeValue>
            </td>
            <td className={bodyCell}>
              <PositiveNegativeValue
                value={holding.detailedReturns.totalGainsPercent}
              >
                {formatterPercentage(holding.detailedReturns.totalGainsPercent)}
              </PositiveNegativeValue>
            </td>
            <td className={bodyCell}></td>
          </>
        )}
      </tr>
    </tbody>
  );
};
